import { useCallback, useEffect, useState } from "react";
import { AtSign, BadgeCheck, Building2, Check, Phone, ShieldCheck, User } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { request } from "@/lib/api";

export default function ProfilePage() {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Mobile is a separate, OTP-verified flow: "idle" -> "code" -> back to idle.
  const [mobileStage, setMobileStage] = useState("idle");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [mobileNotice, setMobileNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await request("/portal/profile", {}, token);
      setProfile(result);
      setName(result.name || "");
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  async function saveName(event) {
    event.preventDefault();
    setSavingName(true);
    setError("");
    setSaved("");
    try {
      const result = await request("/portal/profile", { method: "PUT", body: JSON.stringify({ name: name.trim() }) }, token);
      setProfile((current) => ({ ...current, name: result.name }));
      setSaved("Saved");
      // Clear the confirmation so it reads as a response to this save, not a
      // permanent label on the button.
      setTimeout(() => setSaved(""), 2500);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingName(false);
    }
  }

  async function requestMobileCode(event) {
    event.preventDefault();
    setBusy(true);
    setMobileError("");
    try {
      const result = await request(
        "/portal/profile/mobile/request-otp",
        { method: "POST", body: JSON.stringify({ mobile }) },
        token,
      );
      setMobileNotice(result.message || "");
      setMobileStage("code");
    } catch (requestError) {
      setMobileError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function verifyMobileCode(event) {
    event.preventDefault();
    setBusy(true);
    setMobileError("");
    try {
      const result = await request(
        "/portal/profile/mobile/verify",
        { method: "POST", body: JSON.stringify({ mobile, otp: code }) },
        token,
      );
      setProfile((current) => ({ ...current, mobile: result.mobile }));
      setMobileStage("idle");
      setMobile("");
      setCode("");
      setMobileNotice("");
      setSaved("Mobile number verified");
      setTimeout(() => setSaved(""), 2500);
    } catch (verifyError) {
      setMobileError(verifyError.message);
    } finally {
      setBusy(false);
    }
  }

  function cancelMobileChange() {
    setMobileStage("idle");
    setCode("");
    setMobileError("");
    setMobileNotice("");
  }

  if (!profile) {
    return (
      <section className="profile-page" data-testid="profile-page">
        {error ? <p className="form-error" role="alert" data-testid="profile-error">{error}</p> : <p className="employee-muted">Loading your profile…</p>}
      </section>
    );
  }

  return (
    <section className="profile-page" data-testid="profile-page">
      <p className="eyebrow">Your account</p>
      <h1>Profile</h1>
      <p className="profile-intro">Keep your details up to date so we can reach you about your appointments.</p>

      {error && <p className="form-error" role="alert" data-testid="profile-error">{error}</p>}
      {saved && <p className="profile-saved" role="status" data-testid="profile-saved"><Check size={15} />{saved}</p>}

      {/* Fixed details — shown so people can check them, with a clear route to
          getting them changed rather than a dead end. */}
      <div className="profile-card profile-card--readonly" data-testid="profile-readonly">
        <div className="profile-field">
          <span className="profile-field__label"><AtSign size={14} />Work email</span>
          <strong data-testid="profile-email">{profile.email}</strong>
          <small>Your email identifies your account and can't be changed here. Contact your HR team if it's wrong.</small>
        </div>
        {profile.employee_code && (
          <div className="profile-field">
            <span className="profile-field__label"><BadgeCheck size={14} />Employee ID</span>
            <strong data-testid="profile-employee-code">{profile.employee_code}</strong>
            <small>Set by your organisation.</small>
          </div>
        )}
        {profile.organisation && (
          <div className="profile-field">
            <span className="profile-field__label"><Building2 size={14} />Organisation</span>
            <strong data-testid="profile-org">{profile.organisation}</strong>
          </div>
        )}
      </div>

      <form className="profile-card" onSubmit={saveName} data-testid="profile-name-form">
        <label htmlFor="profile-name"><User size={14} />Full name</label>
        <input
          id="profile-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={200}
          required
          data-testid="profile-name-input"
        />
        <Button type="submit" disabled={savingName || !name.trim() || name.trim() === profile.name} data-testid="profile-save-name">
          {savingName ? "Saving…" : "Save name"}
        </Button>
      </form>

      <div className="profile-card" data-testid="profile-mobile-card">
        <span className="profile-field__label"><Phone size={14} />Mobile number</span>
        {mobileStage === "idle" ? (
          <>
            <strong data-testid="profile-mobile">{profile.mobile || "Not set"}</strong>
            <small>
              {profile.mobile
                ? "You can sign in with this number as well as your email."
                : "Add a verified number to sign in with it instead of your email."}
            </small>
            <form onSubmit={requestMobileCode} className="profile-mobile-form">
              <label htmlFor="profile-mobile-input">{profile.mobile ? "New mobile number" : "Mobile number"}</label>
              <input
                id="profile-mobile-input"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                value={mobile}
                onChange={(event) => setMobile(event.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="9876543210"
                data-testid="profile-mobile-input"
              />
              {mobileError && <p className="form-error" role="alert" data-testid="profile-mobile-error">{mobileError}</p>}
              <Button type="submit" variant="outline" disabled={busy || mobile.length !== 10} data-testid="profile-mobile-request">
                {busy ? "Sending…" : "Send verification code"}
              </Button>
            </form>
          </>
        ) : (
          <form onSubmit={verifyMobileCode} className="profile-mobile-form" data-testid="profile-mobile-verify-form">
            <div className="profile-verify-note"><ShieldCheck size={15} />{mobileNotice || `We've sent a code to ${mobile}.`}</div>
            <label htmlFor="profile-mobile-code">Verification code</label>
            <input
              id="profile-mobile-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              data-testid="profile-mobile-code-input"
            />
            {mobileError && <p className="form-error" role="alert" data-testid="profile-mobile-error">{mobileError}</p>}
            <div className="profile-verify-actions">
              <Button type="button" variant="ghost" onClick={cancelMobileChange} data-testid="profile-mobile-cancel">Cancel</Button>
              <Button type="submit" disabled={busy || code.length !== 6} data-testid="profile-mobile-verify">
                {busy ? "Verifying…" : "Verify and save"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
